import { Dispatch, Store } from "redux";
import BaseContext from "../context/base-context";
import IContextContainer from "../context/icontext-container";
import { BaseService } from "../services/base.service";
import { all } from "redux-saga/effects";
import createSagaMiddleware from "redux-saga";
import { configureStore } from "@reduxjs/toolkit";
import { Entities } from "./entities/entities.enum";
import { Paginations } from "./paginations/paginations.enum";
import { IRootReducer, rootReducer, RESET_STORE } from "./root.reducer";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import createIndexedDBStorage from 'redux-persist-indexeddb-storage';

const indexedDBStorage = createIndexedDBStorage('hg-rock-indexdb', 'hg-rock-store');

const blacklist: string[] = [Entities.MapGeoJson];

const persistConfig = {
  key: 'root',
  storage: indexedDBStorage,
  whitelist: [...Object.values(Entities), 'global'].filter(entity => !blacklist.includes(entity))
};

export class ReduxStore extends BaseContext {
  private _store: any;
  private _persistor: any;
  private _sagas: ReturnType<typeof BaseService.sagas>;
  private _reducers: { [reducerName: string]: any };

  constructor(ctx: IContextContainer) {
    super(ctx);
    this.rootSaga = this.rootSaga.bind(this);
    this._reducers = BaseService.reducers(ctx);
    this._sagas = BaseService.sagas(ctx);
    this._store = this.configureDevStore();
    this._persistor = persistStore(this._store);
  }

  private *rootSaga() {
    yield all(this._sagas);
  }

  private configureProdStore() {
    const sagaMiddleware = createSagaMiddleware();
    const persistedReducer = persistReducer(persistConfig, rootReducer(this._reducers));
    
    const store = configureStore({
      reducer: persistedReducer,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          },
        }).concat(sagaMiddleware),
    });
    sagaMiddleware.run(this.rootSaga);
    return store;
  }

  private configureDevStore = () => {
    const makeStore = () => {
      const sagaMiddleware = createSagaMiddleware();
      const persistedReducer = persistReducer(persistConfig, rootReducer(this._reducers));
      
      const store = configureStore({
        reducer: persistedReducer,
        middleware: (getDefaultMiddleware) =>
          getDefaultMiddleware({
            serializableCheck: {
              ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
          }).concat(sagaMiddleware),
        devTools: process.env.NODE_ENV !== 'production',
      });
      
      (store as any).sagaTask = sagaMiddleware.run(this.rootSaga);
      return store;
    }
    return makeStore();
  };

  public get store(): Store<IRootReducer> {
    return this._store;
  }

  public get persistor() {
    return this._persistor;
  }

  public state = (): IRootReducer => {
    return this._store.getState();
  };

  public dispatch = (args: any): Dispatch => {
    return this._store.dispatch(args);
  };

  public resetStore = () => {
    this._store.dispatch({ type: RESET_STORE });
    this._persistor.purge();
  }

  public getEntityPage<T extends Object>(
    paginationName: Paginations,
    entityName: Entities
  ): T[] {
    if (!this._store) return [];
    const state = this._store.getState();
    const pager = state[paginationName];
    if (!pager || !pager.currentPage || !pager.pages) return [];
    const entities: Object[] = [];
    for (let i = 1; i <= pager.currentPage; ++i) {
      if (!pager.pages[i]) continue;
      pager.pages[i].ids.forEach((entityId: string) => {
        const entity = state[entityName][entityId] as Object;
        entities.push(entity)
      });
    }
    return entities as T[];
  }
}
